'use client';
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { getTheme } from '@/theme/theme';
import { DEFAULT_PREFS, readPrefs, applyPrefs, savePrefs, effectiveDark } from '@/components/g/prefs';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Holds the appearance preferences (theme, accent, density, motion) for the whole app.
 * The values live in one localStorage object and are applied as data attributes on <html>
 * by prefs.js; a head script applies them before the first paint.
 */
export const ThemeProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [resolved, setResolved] = useState('light');
  const pathname = usePathname();

  // Moving between public pages (always light) and the app (user's theme) re-applies the theme.
  useEffect(() => {
    applyPrefs(readPrefs());
  }, [pathname]);

  useEffect(() => {
    const p = readPrefs();
    setPrefs(applyPrefs(p));
    setResolved(effectiveDark(p) ? 'dark' : 'light');
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const cur = readPrefs();
      if (cur.theme === 'system') {
        applyPrefs(cur);
        setResolved(effectiveDark(cur) ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setPref = useCallback((key, value) => {
    const next = savePrefs({ [key]: value });
    setPrefs(next);
    setResolved(effectiveDark(next) ? 'dark' : 'light');
  }, []);
  const setMode = useCallback((t) => setPref('theme', t), [setPref]);
  const toggleTheme = useCallback(() => setMode(resolved === 'dark' ? 'light' : 'dark'), [resolved, setMode]);

  const theme = useMemo(() => getTheme(resolved), [resolved]);
  const value = useMemo(
    () => ({
      prefs,
      setPref,
      mode: prefs.theme, // what the user picked: light | dark | system
      resolved, // what is on screen: light | dark
      theme,
      setMode,
      toggleTheme,
      colors: theme.colors,
    }),
    [prefs, setPref, resolved, theme, setMode, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
